import { BudgetStatus } from "../types/budget/budget";

/** Stay-in-status save: same current and next status (date/justification refresh). */
export function isSameStatusStay(
  currentStatus?: string | null,
  nextStatus?: string | null
): boolean {
  return Boolean(currentStatus && nextStatus && currentStatus === nextStatus);
}

export const BRIEFING_STATUS_OPTIONS = [
  { text: "Aguardando Cliente", value: "WAITING_CLIENT" },
  { text: "Aprovado", value: "APPROVED" },
  { text: "Aprovado/Cancelado", value: "APPROVED_AND_CANCELED" },
  { text: "Auditado", value: "AUDITED" },
  { text: "Cancelado", value: "CANCELED" },
  { text: "Cancelado/Auditado", value: "CANCELED_AND_AUDITED" },
  { text: "Cancelado/Cobrado", value: "CANCELED_AND_CHARGED" },
  { text: "Compartilhado Financeiro", value: "SHARED_FINANCIAL" },
  { text: "Declinado", value: "DECLINED" },
  { text: "Duplicidade", value: "DUPLICATE" },
  { text: "Em Aprovação", value: "ON_APROVAL" },
  { text: "Faturado", value: "BILLED" },
  { text: "Finalizado", value: "FINISHED" },
  { text: "Faturamento", value: "FINANCIAL" },
  { text: "Liberado Operação", value: "CLEARED_OPERATIONS" },
  { text: "Orçamento", value: "BUDGET" },
  { text: "Perdido", value: "LOST" },
  { text: "Prestação de Contas", value: "LEGAL_REPORTING" },
  { text: "Pré-fechamento", value: "PRE_CLOSING" },
  { text: "Faturado/Pendente Auditoria", value: "BILLED_PENDING_AUDIT" },
  { text: "BID", value: "BID" },
];

export const ALLOWED_STATUS_TRANSITION: { [index: string]: string[] } = {
  BUDGET: [
    "ON_APROVAL",
    "DUPLICATE",
    "SHARED_FINANCIAL",
    "CANCELED",
    "DECLINED",
    "BID",
  ],
  ON_APROVAL: [
    "APPROVED",
    "DECLINED",
    "LOST",
    "CANCELED",
    "BUDGET",
    "DUPLICATE",
  ],
  APPROVED: [
    "APPROVED_AND_CANCELED",
    "CANCELED",
    "FINISHED",
    "ON_APROVAL",
    "DUPLICATE",
  ],
  APPROVED_AND_CANCELED: ["CANCELED_AND_CHARGED", "APPROVED"],
  CANCELED_AND_CHARGED: ["CANCELED_AND_AUDITED", "APPROVED_AND_CANCELED"],
  CANCELED_AND_AUDITED: ["CANCELED_AND_CHARGED"],
  FINISHED: ["PRE_CLOSING", "APPROVED"],
  PRE_CLOSING: ["CLEARED_OPERATIONS", "FINISHED"],
  CLEARED_OPERATIONS: ["WAITING_CLIENT", "PRE_CLOSING"],
  WAITING_CLIENT: ["FINANCIAL", "CLEARED_OPERATIONS"],
  FINANCIAL: ["LEGAL_REPORTING", "WAITING_CLIENT"],
  LEGAL_REPORTING: ["BILLED", "FINANCIAL"],
  BILLED: ["BILLED_PENDING_AUDIT", "LEGAL_REPORTING"],
  BILLED_PENDING_AUDIT: ["AUDITED", "BILLED"],
  AUDITED: ["BILLED_PENDING_AUDIT"],
  SHARED_FINANCIAL: ["CANCELED", "BUDGET"],
  DUPLICATE: ["BUDGET"],
  CANCELED: ["BUDGET", "ON_APROVAL", "APPROVED", "SHARED_FINANCIAL"],
  DECLINED: ["BUDGET", "ON_APROVAL"],
  LOST: ["ON_APROVAL"],
  BID: ["BUDGET"],
};

export const ALLOWED_STATUS_RETURN: { [index: string]: any } = {
  ON_APROVAL: "BUDGET",
  APPROVED: "ON_APROVAL",
  APPROVED_AND_CANCELED: "APPROVED",
  CANCELED_AND_CHARGED: "APPROVED_AND_CANCELED",
  CANCELED_AND_AUDITED: "CANCELED_AND_CHARGED",
  FINISHED: "APPROVED",
  PRE_CLOSING: "FINISHED",
  CLEARED_OPERATIONS: "PRE_CLOSING",
  WAITING_CLIENT: "CLEARED_OPERATIONS",
  FINANCIAL: "WAITING_CLIENT",
  LEGAL_REPORTING: "FINANCIAL",
  BILLED: "LEGAL_REPORTING",
  BILLED_PENDING_AUDIT: "BILLED",
  AUDITED: "BILLED_PENDING_AUDIT",
  SHARED_FINANCIAL: "BUDGET",
  DUPLICATE: "BUDGET",
  CANCELED: ["BUDGET", "ON_APROVAL", "APPROVED", "SHARED_FINANCIAL"],
  DECLINED: ["BUDGET", "ON_APROVAL"],
  LOST: "ON_APROVAL",
  BID: "BUDGET",
};

export const TRANSITION_REQUIREMENTS: {
  [index: string]: {
    summary: any;
    address: boolean;
    summaryException?: any;
    // Exige orçamento/negociação na etapa de Fechamento (CLOSURE)
    closingBudget?: boolean;
    // Exige que o Pré-Fechamento (closingControl) já tenha sido enviado
    requiresPreClosing?: boolean;
  };
} = {
  WAITING_CLIENT: {
    summary: BudgetStatus.CLOSURE,
    summaryException: "CLEARED_OPERATIONS",
    address: true,
  },
  // Aprovar o projeto não exige orçamento/negociação em Fechamento (trava
  // removida em MMV-2544): bastam os dados do local e o resumo financeiro.
  APPROVED: { summary: null, address: true },
  // Cancelamento não deve exigir o resumo financeiro/endereço completos da aprovação.
  APPROVED_AND_CANCELED: { summary: null, address: false },
  AUDITED: {
    summary: BudgetStatus.CLOSURE,
    summaryException: "BILLED_PENDING_AUDIT",
    address: true,
  },
  CANCELED: { summary: null, address: false },
  CANCELED_AND_AUDITED: { summary: BudgetStatus.APPROVED, address: true },
  CANCELED_AND_CHARGED: { summary: BudgetStatus.APPROVED, address: true },
  SHARED_FINANCIAL: { summary: null, address: false },
  DECLINED: {
    summary: BudgetStatus.ON_APPROVAL,
    summaryException: ["BUDGET", "ON_APROVAL"],
    address: false,
  },
  DUPLICATE: { summary: null, address: false },
  ON_APROVAL: { summary: null, address: false },
  BILLED: {
    summary: BudgetStatus.CLOSURE,
    summaryException: "LEGAL_REPORTING",
    address: true,
  },
  BILLED_PENDING_AUDIT: {
    summary: BudgetStatus.CLOSURE,
    summaryException: "BILLED",
    address: true,
  },
  FINISHED: {
    summary: BudgetStatus.APPROVED,
    summaryException: "APPROVED",
    address: true,
  },
  FINANCIAL: {
    summary: BudgetStatus.CLOSURE,
    summaryException: "WAITING_CLIENT",
    address: true,
  },
  CLEARED_OPERATIONS: {
    summary: BudgetStatus.CLOSURE,
    summaryException: "PRE_CLOSING",
    address: true,
  },
  BUDGET: { summary: null, address: false },
  LOST: {
    summary: BudgetStatus.ON_APPROVAL,
    summaryException: "ON_APROVAL",
    address: false,
  },
  LEGAL_REPORTING: {
    summary: BudgetStatus.CLOSURE,
    summaryException: "FINANCIAL",
    address: true,
  },
  // De Finalizado: não exige revalidar todo o resumo "Aprovado" (fees, RSVP, etc.)
  PRE_CLOSING: {
    summary: BudgetStatus.APPROVED,
    summaryException: "FINISHED",
    address: true,
    requiresPreClosing: true,
  },
  BID: { summary: null, address: false },
};
